import * as React from 'react';
import { Scheduler, TimelineView, DayView, WeekView, MonthView, SchedulerDataChangeEvent, SchedulerResource, SchedulerViewItem, SchedulerViewItemProps } from '@progress/kendo-react-scheduler';
import { Day } from "@progress/kendo-date-math";
import { JobDataItem, resourceData } from '../../pages/Diary/Diary';
import { dropDown, scheduledEvent } from '../../data/interfaces';
import '@progress/kendo-date-math/tz/Europe/London';
import { ListViewHeader } from '@progress/kendo-react-listview';
import { FormDateTimePicker, FormMultiSelect } from '../FormComps/FormComponents';
import { MultiSelectChangeEvent } from '@progress/kendo-react-dropdowns';
import { getEmployeeDropdown } from '../../data/employeeData';
import { Dialog } from '@progress/kendo-react-dialogs';
import { Field, Form, FormElement } from '@progress/kendo-react-form';
import { deleteAllFutureJobs, getTargetEvent, getTargetJob } from '../../data/jobData';
import { FormWithCustomEditor } from './custom-form';
import { FaRedoAlt, FaTrash } from 'react-icons/fa';
import { IntlProvider, LocalizationProvider } from '@progress/kendo-react-intl';
import { useSearchParams } from 'react-router-dom';


interface EventJobEdit {
  id: number;
  siteAddr: string;
  client: string;
  description: string;
  employeeProjectmanager: string;
  start: Date;
  end: Date;
  lastScheduledBy: string;
  assignedEmployees: {
    text: string;
    value: number;
  }[];
}

interface EventEdit {
  appointmentId: number;
  jobId: number;
  startDate: Date;
  endDate: Date;
  assignedEmployees: {
    text: string;
    value: number;
  }[];
}

interface Props {
  lockEdit: boolean;
  resources: resourceData[]
  scheduleData: scheduledEvent[]
  unscheduledData: JobDataItem[]
  onFilterDataChange: (ids: number[]) => void
  refreshData: () => void
  onEventEditSubmit: (id: number, newStartDate: Date, newEndDate: Date, employeeId: number[]) => void
  getScheduledEvents: () => void
  onRemoveItem: (id: number) => void
}

const EventCalendar2 = ({ resources, scheduleData, unscheduledData, onFilterDataChange, refreshData, onEventEditSubmit, getScheduledEvents, onRemoveItem, lockEdit }: Props) => {
  /** State to hold the formatted resources */
  const [formattedResources, setFormattedResources] = React.useState<SchedulerResource>();
  /** State to hold an array of filtered employee ID's from the multiselect  */
  const [employeeFilter, setEmployeeFilter] = React.useState<number[]>([]);
  /** State to hold a list of employees for the filter drop down */
  const [employeeData, setEmployeeData] = React.useState<dropDown[]>([]);
  /** Number of days to show in the timeline */
  const [numberOfDays, setNumberOfDays] = React.useState<number>(7);

  /** States for editing */
  const [selectedEventInfo, setSelectedEventInfo] = React.useState<EventEdit | null>(null);
  const [selectedEventJobInfo, setSelectedEventJobInfo] = React.useState<EventJobEdit | null>(null);
  const [visibleEventModal, setVisibleEventModal] = React.useState<boolean>(false)
  const [loadingModal, setLoadingModal] = React.useState<boolean>(false);

  const [deleteAllBookingsConfirmed, setDeleteAllBookingsConfirmed] = React.useState<boolean>(false);
  const [jobId, setJobId] = React.useState<number | null>(null);   

  /**
   * Returns the current date, adjusted to the last Monday.
   * @returns {Date} The current date adjusted to the last Monday.
   */
  const getCurrentDate = (): Date => {
    const now = new Date();
    const dayOfWeek = now.getDay();
    const daysToLastMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    now.setDate(now.getDate() - daysToLastMonday);
    now.setHours(0, 0, 0, 0);
    return now;
  }

  /**
   * Transforms resource data to a scheduler resource event.
   * @param resourceData The resource data to transform.
   * @returns The transformed scheduler resource.
   */
  const transformResourceDataToResourceEvent = (resourceData: resourceData[]): SchedulerResource => {
    const resources = resourceData.map(resource => {
      return {
        text: (
          <div style={{ display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center", height: "auto" }}>
            <div style={{
              border: resource.departmentColourHex === '#fff' ? '1px solid black' : 'none',
              backgroundColor: `${resource.departmentColourHex}`,
              width: "10px",
              height: "30px",
              borderRadius: "1em",
              marginRight: "0.5em"
            }}>
            </div>
            <div>{resource.employeeName}</div>
          </div>
        ),
        value: resource.employeeId,
        color: resource.departmentColourHex
      };
    });
    return {
      name: "Employees",
      data: resources,
      field: 'employeeIds',
      valueField: "value",
      textField: "text",
      colorField: 'color'
    };
  }

  /**
   * Handles the data change event for the EventCalendar2 component.
   * 
   * @param e - The SchedulerDataChangeEvent object containing the event data.
   * @returns A boolean value indicating whether the data change was successful.
   */
  const handleDataChange = ({ updated, deleted }: SchedulerDataChangeEvent) => {

    // Handling deleted items
    if (deleted.length > 0) {
      deleted.forEach(deletedEvent => {
        onRemoveItem(deletedEvent.id);
      });
    }


  };

  /**
   * Handles filtering data based on the provided filter IDs.
   * @param filterIds - An array of filter IDs.
   */
  const handleFilterData = (filterIds: number[]) => {
    onFilterDataChange(filterIds);
    console.log(filterIds);
  }

  /**
   * Handles the event when a filtered employee is added.
   * 
   * @param event - The event object containing the selected employees.
   */
  const handleAddFilteredEmployee = (event: MultiSelectChangeEvent) => {
    const filterIds = event.value.map((employee: any) => employee.value);
    setEmployeeFilter(filterIds);
  };

  /**
   * Toggles the visibility of the event modal.
   */
  const handleEventModalToggle = () => {
    setVisibleEventModal(!visibleEventModal);
    setDeleteAllBookingsConfirmed(false);
  };

  /**
   * Handles the submission of the event modal form.
   * 
   * @param dataItem - The data item containing the event details.
   */
  const handleEventModalSubmit = (dataItem: any) => {
    if (selectedEventInfo === null) return;

    dataItem.start = dataItem.startDate;
    dataItem.end = dataItem.endDate;
    const employeeIds = dataItem.assignedEmployees.map((employee: any) => employee.value);
    onEventEditSubmit(selectedEventInfo.appointmentId, dataItem.start, dataItem.end, employeeIds);
    setVisibleEventModal(!visibleEventModal);
  };

  /**
   * Fetches job information for the selected event.
   * @param id - The ID of the selected event.
   * @returns An object containing the fetched job information.
   */
  const fetchJobInfoForSelectedEvent = async (id: number) => {
    const jobData = await getTargetJob(id);
    const eventDataMap = {
      id: jobData.jobId,
      siteAddr: jobData.site.siteAddr + " - " + jobData.site.postcode,
      client: jobData.client.clientName,
      description: jobData.description,
      employeeProjectmanager: jobData.employeeProjectmanager.employeeName,
      start: new Date(jobData.scheduledStartDate),
      end: new Date(jobData.scheduledEndDate),
      lastScheduledBy: jobData.lastScheduledBy,
      assignedEmployees: jobData.employees.map((employee) => ({
        text: employee.employeeName,
        value: employee.employeeId
      }))
    };

    setJobId(jobData.jobId);

    return eventDataMap;
  }

  /**
   * Fetches information for the selected event.
   * @param id - The ID of the selected event.
   * @returns An object containing the fetched event data.
   */
  const fetchInfoForSelectedEvent = async (id: number) => {
    // Fetch data for the selected event
    const eventData = await getTargetEvent(id);

    const eventDataMap = {
      appointmentId: eventData.appointmentId,
      jobId: eventData.jobId,
      startDate: eventData.startDate,
      endDate: eventData.endDate,
      assignedEmployees: eventData.assignedEmployees.map((employee) => ({
        text: employee.employeeName,
        value: employee.employeeId
      }))
    };

    return eventDataMap;
  }

  /**
   * Custom view item component for the event calendar.
   * 
   * @param props - The props for the SchedulerViewItem component.
   * @returns The JSX element representing the custom view item.
   */

  const handleDoubleClick = async (props?: SchedulerViewItemProps, appointmentId?: string, jobId?: string) => {
    try {
      setLoadingModal(true);
      fetchInfoForSelectedEvent(props ? props.dataItem.id : appointmentId).then((result) => {
        console.log(result)
        setSelectedEventInfo(result as any);
      }).catch((error) => {
        console.error(error);
      })
     fetchJobInfoForSelectedEvent(props ? props.dataItem.jobId : jobId).then((result) => {
        setSelectedEventJobInfo(result);
        setLoadingModal(false);
      }).catch(err => console.log(err))

      handleEventModalToggle();
    } catch (error) {
      console.error(error);
    }
  };

  const CustomViewItem = (props: SchedulerViewItemProps) => {
    const [initials, ...restOfTitle] = props.dataItem.title.split(' - ');

    return (
      <SchedulerViewItem
        {...props}
        style={{ ...props.style, height: "35px" }}
        onDoubleClick={() => handleDoubleClick(props)}
      >
        <div style={{ fontWeight: 'bold', marginRight: '5px' }}>{initials}</div>
        <span>{restOfTitle.join(' - ')}</span>
      </SchedulerViewItem>
    );
  };


  /**
   * CustomDateHeaderCell component for rendering a custom date header cell in the event calendar.
   * @param props - The component props.
   * @param props.date - The date to be formatted and displayed in the header cell.
   * @returns The JSX element representing the custom date header cell.
   */
  const CustomDateHeaderCell = (props: any) => {
    const formatter = new Intl.DateTimeFormat('en', { weekday: 'short', day: '2-digit', month: 'short' });
    // Using Intl.DateTimeFormat to format the date
    const formattedDate = formatter.format(props.date);

    return (
      <div style={{ width: "100%", padding: "1em", borderBottom: "1px solid lightgrey", borderRight: "1px solid lightgrey" }}>
        <p style={{ fontWeight: "600", margin: 0, fontSize: "9pt" }}>{formattedDate}</p>
      </div>
    );
  };

  /**
   * Format the resources prop whenever it changes, then store it in state
   */
  React.useEffect(() => {
    if (resources) {
      setFormattedResources(transformResourceDataToResourceEvent(resources));
    }
  }, [resources]);

  React.useEffect(() => {

    const fetchEmployeeData = async () => {
      const data = await getEmployeeDropdown('')
      setEmployeeData(data)
    }

    fetchEmployeeData();
  }, [scheduleData, unscheduledData]);

  const deleteAllBookings = async () => {
    if(jobId){
      deleteAllFutureJobs(jobId)
      .then(() => {
        setVisibleEventModal(false);
        getScheduledEvents();
      })
      .catch((error) => {
        console.error(error);
      })
    }
  }

  /**
   * Hack to fix the timeline view showing bookings out of line with the assigned person (rendering issue)
   * The fix sets the container to be 1px bigger than it should be, then after 1 second, sets it to 100%.
   * The container resize should trigger a re-render of the scheduler, fixing the issue.
   * 
   * At the point of creating this, I see no issue listed online (5/07/2024)
   */
  const [resizeFix, setResizeFix] = React.useState<string>("calc(100% + 5px)");

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setResizeFix("100%");
    }, 2000)
    return () => clearTimeout(timer)
  }, []);


  const [searchParams] = useSearchParams();
  React.useEffect(() => {
    const jobId = searchParams.getAll("jobId")[0];
    const appointmentId = searchParams.getAll("appointmentId")[0];
    if(jobId && appointmentId){
      handleDoubleClick(undefined, appointmentId, jobId);
    }
  }, [])   

  return (
    <div>

      {
        formattedResources && scheduleData ? (
          <div style={{ width: "100%", margin: "10px 0 0 0", display: "flex", flexDirection: "column" }}>
            <ListViewHeader style={{ display: "flex", justifyContent: "flex-end", marginBottom: "0.5em" }} className="diaryListHeader diaryListText">
              <div style={{ display: 'flex', alignItems: 'center', width: "30%", justifyContent: "flex-end" }}>
                <div style={{ display: "flex", flexDirection: "row" }}>
                  <FormMultiSelect
                    style={{ width: "400px", height: "40px", marginRight: "0.5em", fontSize: "9pt !important", fontFamily: "Heebo, sans-serif" }}
                    onChange={handleAddFilteredEmployee}
                    data={employeeData}
                    placeholder="Filter by Employee..."
                  />
                  <button
                    style={{
                      border: 0,
                      borderRadius: "0.5em",
                      fontFamily: "Heebo, sans-serif",
                      fontSize: "9pt",
                      width: "100px",
                    }}
                    onClick={() => { handleFilterData(employeeFilter) }}
                  >
                    Filter
                  </button>
                </div>
              </div>
            </ListViewHeader>
            <div style={{ color: "black", margin: "0 0 10px 0", padding: "10px", display: "flex", gap: "15px", alignItems: "center", background: "white", width: "100%" }}>
              {/* <div>Toggle week view</div> */}
              {/* <Switch
                onLabel='Show full week'
                offLabel='Show work week'
                checked={numberOfDays === 7}
                onChange={(event) => setNumberOfDays(event.value ? 7 : 5)} /> */}
              <div style={{ margin: "0 0 0 auto" }}>
                {/* <button
                  style={{
                    border: 0,
                    borderRadius: "0.5em",
                    fontFamily: "Heebo, sans-serif",
                    fontSize: "9pt",
                    width: "100px",
                  }}
                  onClick={() => refreshData()}
                >
                </button>               */}
                <FaRedoAlt style={{ margin: "0 10px 0 0", cursor: "pointer" }} title={"Refresh scheduler"} onClick={() => refreshData()} />
              </div>
            </div>
            <div style={{ width: "100%" }}>
              <LocalizationProvider language={"en-UK"}>
                <IntlProvider locale={"en-UK"}>
                  <Scheduler
                    data={scheduleData}
                    onDataChange={handleDataChange}
                    defaultView='timelineWeek'
                  timezone='Europe/London'
                  viewItem={CustomViewItem}
                  defaultDate={getCurrentDate()}
                  form={(props) => FormWithCustomEditor(props, getScheduledEvents)}
                  // view={view}
                  // onViewChange={handleViewChange}

                  // date={date}
                  // onDateChange={handleDateChange}

                  editable={{
                    drag: false,
                    remove: lockEdit ? false : true,
                    resize: false,
                    add: lockEdit ? false : true,
                    edit: false,
                    select: false,
                  }}
                  // timezone={timezone}
                  // modelFields={customModelFields}

                  group={{
                    resources: ['Employees'],
                    orientation: "vertical"
                  }}
                  resources={[formattedResources]}
                >
                  <TimelineView
                    numberOfDays={numberOfDays}
                    slotDivisions={2}
                    slotDuration={620}
                    workDayStart='07:00'
                    workDayEnd='17:00'
                    step={7}
                    workWeekStart={Day.Monday}
                    workWeekEnd={Day.Monday}
                    dateHeaderCell={CustomDateHeaderCell}
                  />
                  <DayView
                    slotDivisions={1}
                    numberOfDays={3}
                    workDayStart='07:00'
                    workDayEnd='17:00'
                    dateHeaderCell={CustomDateHeaderCell}
                  />
                  <WeekView
                    workDayStart='07:00'
                    workDayEnd='17:00'
                    step={1}
                    dateHeaderCell={CustomDateHeaderCell}
                  />
                  <MonthView
                    itemsPerSlot={4} />
                </Scheduler>
              </IntlProvider>
            </LocalizationProvider>
            </div>
              </div>
        ) : null
      }
      {visibleEventModal && (
        <Dialog
          width={500}
          height={850}
          onClose={handleEventModalToggle}
        >
          {
            loadingModal ?
              (
                <div>Loading...</div>
              )
              :
              (
                <Form
                  initialValues={{
                    projectManager: selectedEventJobInfo?.employeeProjectmanager,
                    siteAddr: selectedEventJobInfo?.siteAddr,
                    client: selectedEventJobInfo?.client,
                    description: selectedEventJobInfo?.description,
                    startDate: selectedEventInfo?.startDate,
                    endDate: selectedEventInfo?.endDate,
                    assignedEmployees: selectedEventInfo?.assignedEmployees
                  }}
                  onSubmit={handleEventModalSubmit}
                  render={() => (
                    <FormElement style={{ padding: '1rem', width: '100%' }}>
                      <fieldset className={'k-form-fieldset'} style={{ display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                        <div style={{ display: "flex", flexDirection: "column", marginTop: "0.5em" }}>
                          <div className='form__title__background'>
                            <div className='form__subtitle'>
                              Amend event:
                            </div>
                          </div>

                          <div className='event__info__container'>
                            <div className='event__info__subheader'>Project manager: <br /></div>
                            <div style={{ margin: 0 }} className='event__info__text'>{selectedEventJobInfo?.employeeProjectmanager}</div>
                            <div style={{ marginTop: "0.5em" }} className='event__info__subheader'>Site address: <br /> </div>
                            <div style={{ margin: 0 }} className='event__info__text'>{selectedEventJobInfo?.siteAddr}</div>
                            <div style={{ marginTop: "0.5em" }} className='event__info__subheader'>Client: <br /></div>
                            <div style={{ margin: 0 }} className='event__info__text'>{selectedEventJobInfo?.client}</div>
                            <div style={{ marginTop: "0.5em" }} className='event__info__subheader'>Description: <br /> </div>
                            <div style={{ margin: 0 }} className='event__info__text'>{selectedEventJobInfo?.description}</div>

                            <div style={{ fontStyle: "italic", fontSize: "9pt", color: "#bfbfbf", marginTop: "1em" }} className='event__info__text'>Last modified by: {selectedEventJobInfo?.lastScheduledBy}</div>
                          </div>

                          <Field
                            disabled={lockEdit}
                            name={'startDate'}
                            component={FormDateTimePicker}
                            label={'Choose start date:'}
                            format={'dd/MM/yyyy HH:mm'}
                          />

                          <Field
                            disabled={lockEdit}
                            name={'endDate'}
                            component={FormDateTimePicker}
                            label={'Choose end date:'}
                            format={'dd/MM/yyyy HH:mm'}
                          />


                          {/* <Field
                          data={employeeData}
                          originalData={employeeDataOrig}
                          name={'assignedEmployees'}
                          component={FormMultiSelect}
                          searchDropdown={searchEmployeeDropdown}
                          label={'Choose assigned employees:'}
                          value={selectedEventInfo?.assignedEmployees}
                        /> */}
                        </div>

                        <div style={{ display: "flex", flexDirection: "column", marginTop: "3em" }}>
                          {
                            lockEdit === false ? (
                              <button type='submit' className='form__edit__button accept' style={{ width: "100%" }}>
                                Amend event
                              </button>
                            ) : null
                          }
                          <button onClick={handleEventModalToggle} className='form__edit__button edit' style={{ width: "100%" }}>
                            Cancel
                          </button>
                          {
                            deleteAllBookingsConfirmed && lockEdit === false ? (
                              <>
                              <span style={{textAlign: "center"}}>Are you sure you want to delete all future bookings? This action cannot be undone.</span>
                              <button onClick={() => deleteAllBookings()} className='form__edit__button reject' style={{ width: "100%", marginTop: "10px" }}>
                                Yes, I want to delete all future bookings
                              </button>
                            
                              </>
                            ) : lockEdit === false?  (
                              <div onClick={() => setDeleteAllBookingsConfirmed(true)} style={{color: "rgb(220, 67, 67)", display: "flex", alignItems: "center", gap: "10px", justifyContent: "center", marginTop: "20px", cursor: "pointer"}}><FaTrash /><span>Delete all bookings</span></div>
                            ) : null
                          }

                        </div>
                      </fieldset>
                    </FormElement>
                  )}
                />
              )
          }
        </Dialog>
      )}

    </div>
  )
}

export default EventCalendar2;
